import type { ReactNode } from 'react'

import {
  DesignSystemProvider,
  type DesignSystemProviderProps,
} from './design-system-provider'
import type { DesignSystemContextValue } from './ds-context'

/**
 * Full DS wrapper for tests / stories — includes the emotion theme so
 * components that read `theme.palette.*` / `theme.spacing.*` render
 * correctly. Built on top of the public `DesignSystemProvider` with
 * sensible CZ defaults; pass `value` to override any field.
 *
 * Use this in *integration* tests that render real components. For
 * pure context-only tests (no theme), `TestDesignSystemContextProvider`
 * is lighter.
 */
const defaultTestValue: DesignSystemContextValue = {
  currencyCode: 'CZK',
  callingCodes: ['+420', '+421'],
  formattedOptions: {
    presets: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      zipCode: {} as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      personalId: {} as any,
      date: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cs: {} as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pl: {} as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        en: {} as any,
      },
      money: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cs: { delimiter: ' ' } as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pl: { delimiter: ' ' } as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        en: { delimiter: ',' } as any,
      },
    },
    currency: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cs: { decimalSeparator: ',', thousandSeparator: ' ' } as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pl: { decimalSeparator: ',', thousandSeparator: ' ' } as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      en: { decimalSeparator: '.', thousandSeparator: ',' } as any,
    },
  },
  supportEmail: 'support@example.test',
  support: {
    email: 'support@example.test',
    phoneNumber: '+420 000 000 000',
    hoursFrom: 9,
    hoursTo: 18,
    days: 'mo-fr',
  },
}

type Props = {
  children: ReactNode
  /** Override any field of the test context. */
  value?: Partial<DesignSystemContextValue>
} & Pick<DesignSystemProviderProps, 'themeConfig' | 'themeMode' | 'emotionCache'>

export const TestDesignSystemProvider = ({
  children,
  value,
  themeConfig,
  themeMode,
  emotionCache,
}: Props) => (
  <DesignSystemProvider
    {...defaultTestValue}
    {...value}
    emotionCache={emotionCache}
    themeConfig={themeConfig}
    themeMode={themeMode}
  >
    {children}
  </DesignSystemProvider>
)
